import { AnalyticEvent, AnalyticEventsDataTypes, CustomEventName } from '@sweatpals/shared';

export type AnalyticGtmEventsDataTypes = {
	event_page_view: {
		event_id: string;
		event_name: string;
		customer_email?: string;
		customer_phone?: string;
		customer_id?: string;
		event_visibility: string;
		event_bundle?: boolean;
		membership_status?: string;
		community_status?: string;
		organizer_id: string;
		organizer_name?: string;
		organizer_type?: string;
	};
	add_ticket_to_cart: {
		event_id: string;
		event_name: string;
		value: string;
		currency: string;
		ticket_quantity: string;
		customer_email?: string;
		customer_phone?: string;
		customer_id?: string;
		event_visibility: string;
		event_bundle?: boolean;
		membership_status?: string;
		community_status?: string;
		organizer_id: string;
		organizer_name?: string;
		organizer_type?: string;
		items: {
			ticket_value: string;
			ticket_type: string;
			ticket_tier_name: string;
			ticket_tier_type: string;
			ticket_name: string;
			ticket_id: string;
		}[];
	};
	begin_checkout: {
		event_id: string;
		event_name: string;
		value: string;
		currency: string;
		ticket_quantity: string;
		customer_email?: string;
		customer_phone?: string;
		event_visibility: string;
		event_bundle?: boolean;
		customer_id?: string;
		membership_status?: string;
		community_status?: string;
		organizer_id: string;
		organizer_name?: string;
		organizer_type?: string;
		items: {
			ticket_value: string;
			ticket_type: string;
			ticket_tier_name: string;
			ticket_tier_type: string;
			ticket_name: string;
			ticket_id: string;
		}[];
	};
	event_purchase: {
		event_id: string;
		event_name: string;
		value: string;
		currency: string;
		ticket_quantity: string;
		customer_email?: string;
		customer_phone?: string;
		event_visibility: string;
		event_bundle?: boolean;
		customer_id?: string;
		membership_status?: string;
		community_status?: string;
		organizer_id: string;
		organizer_name?: string;
		organizer_type?: string;
		transaction_id: string;
		tax: string;
		new_or_returning_purchase?: string;
		items?: {
			ticket_value: string;
			ticket_type: string;
			ticket_tier_name: string;
			ticket_tier_type: string;
			ticket_name: string;
			ticket_id: string;
		}[];
	};
	initiate_event_creation: {
		customer_email?: string;
		customer_phone?: string;
		customer_id?: string;
		membership_status?: string;
		organizer_id: string;
		organizer_name?: string;
		organizer_type?: string;
	};
	event_published: {
		event_id: string;
		event_name: string;
		event_visibility: string;
		event_bundle?: boolean;
		event_promo_status?: string;
		new_or_returning_host?: string;
		ticket_type: string;
		customer_email?: string;
		customer_phone?: string;
		customer_id?: string;
		membership_status?: string;
		organizer_id: string;
		organizer_name?: string;
		organizer_type?: string;
	};
};

export const gtmAnalyticsTrackingDataMapper = <T extends CustomEventName<AnalyticEventsDataTypes>>(
	event: T,
	props?: AnalyticEvent<T, AnalyticEventsDataTypes>
): Record<string, unknown> => {
	let payload: Record<string, unknown> = {};

	switch (event) {
		case 'event_page_view': {
			const {
				baseEventId,
				eventInstance,
				authorId,
				eventName,
				userId,
				userLogin,
				userEmail,
				eventVisibility,
				authorName,
				eventBundle,
			} = (props ?? {}) as AnalyticEventsDataTypes['event_page_view'];

			const data: AnalyticGtmEventsDataTypes['event_page_view'] = {
				// membership_status
				// community_status
				// organizer_type
				event_id: `${baseEventId}-${eventInstance}`,
				event_name: eventName,
				...(userEmail ? { customer_email: userEmail } : {}),
				...(userLogin ? { customer_phone: userLogin } : {}),
				...(userId ? { customer_id: userId } : {}),
				event_visibility: eventVisibility,
				event_bundle: eventBundle,
				organizer_id: authorId,
				...(authorName ? { organizer_name: authorName } : {}),
			};
			payload = data;
			break;
		}
		case 'add_ticket_to_cart': {
			const {
				eventName,
				baseEventId,
				eventInstance,
				ticketQuantity,
				eventVisibility,
				currency,
				value,
				items,
				eventBundle,
				userId,
				userLogin,
				userEmail,
				authorId,
				authorName,
			} = (props ?? {}) as AnalyticEventsDataTypes['add_ticket_to_cart'];

			const data: AnalyticGtmEventsDataTypes['add_ticket_to_cart'] = {
				value,
				currency,
				ticket_quantity: ticketQuantity,
				event_id: `${baseEventId}-${eventInstance}`,
				event_name: eventName,
				...(userEmail ? { customer_email: userEmail } : {}),
				...(userLogin ? { customer_phone: userLogin } : {}),
				...(userId ? { customer_id: userId } : {}),
				event_visibility: eventVisibility,
				event_bundle: eventBundle,
				organizer_id: authorId,
				...(authorName ? { organizer_name: authorName } : {}),
				items: items.map(item => ({
					ticket_value: item.ticketValue,
					ticket_type: item.ticketType,
					ticket_tier_name: item.ticketTierName,
					ticket_tier_type: item.ticketTierType,
					ticket_name: item.ticketName,
					ticket_id: item.ticketId,
				})),
			};

			payload = data;
			break;
		}
		case 'begin_checkout': {
			const {
				eventName,
				baseEventId,
				eventInstance,
				ticketQuantity,
				eventVisibility,
				currency,
				value,
				items,
				eventBundle,
				userId,
				userLogin,
				userEmail,
				authorId,
				authorName,
			} = (props ?? {}) as AnalyticEventsDataTypes['begin_checkout'];

			const data: AnalyticGtmEventsDataTypes['begin_checkout'] = {
				value,
				currency,
				ticket_quantity: ticketQuantity,
				event_id: `${baseEventId}-${eventInstance}`,
				event_name: eventName,
				...(userEmail ? { customer_email: userEmail } : {}),
				...(userLogin ? { customer_phone: userLogin } : {}),
				...(userId ? { customer_id: userId } : {}),
				event_visibility: eventVisibility,
				event_bundle: eventBundle,
				organizer_id: authorId,
				...(authorName ? { organizer_name: authorName } : {}),
				items: items.map(item => ({
					ticket_value: item.ticketValue,
					ticket_type: item.ticketType,
					ticket_tier_name: item.ticketTierName,
					ticket_tier_type: item.ticketTierType,
					ticket_name: item.ticketName,
					ticket_id: item.ticketId,
				})),
			};

			payload = data;
			break;
		}
		case 'event_purchase': {
			const {
				eventName,
				baseEventId,
				eventInstance,
				ticketQuantity,
				eventVisibility,
				currency,
				amount,
				items,
				eventBundle,
				authorId,
				tax,
				transactionId,
				userId,
				userLogin,
				userEmail,
				authorName,
			} = (props ?? {}) as AnalyticEventsDataTypes['event_purchase'];

			const data: AnalyticGtmEventsDataTypes['event_purchase'] = {
				value: (amount / 100).toFixed(2),
				currency,
				ticket_quantity: ticketQuantity,
				event_id: `${baseEventId}-${eventInstance}`,
				event_name: eventName,
				...(userEmail ? { customer_email: userEmail } : {}),
				...(userLogin ? { customer_phone: userLogin } : {}),
				...(userId ? { customer_id: userId } : {}),
				event_visibility: eventVisibility,
				event_bundle: eventBundle,
				organizer_id: authorId,
				...(authorName ? { organizer_name: authorName } : {}),
				transaction_id: transactionId,
				tax,
				...(items
					? {
							items: items.map(item => ({
								ticket_value: item.ticketValue,
								ticket_type: item.ticketType,
								ticket_tier_name: item.ticketTierName,
								ticket_tier_type: item.ticketTierType,
								ticket_name: item.ticketName,
								ticket_id: item.ticketId,
							})),
						}
					: {}),
			};

			payload = data;
			break;
		}
		case 'initiate_event_creation': {
			const { userId, userLogin, userEmail, authorId, authorName } = (props ??
				{}) as AnalyticEventsDataTypes['initiate_event_creation'];

			const data: AnalyticGtmEventsDataTypes['initiate_event_creation'] = {
				...(userEmail ? { customer_email: userEmail } : {}),
				...(userLogin ? { customer_phone: userLogin } : {}),
				...(userId ? { customer_id: userId } : {}),
				organizer_id: authorId,
				...(authorName ? { organizer_name: authorName } : {}),
			};
			payload = data;
			break;
		}
		case 'event_published': {
			const {
				baseEventId,
				eventInstance,
				eventName,
				eventVisibility,
				eventBundle,
				ticketType,
				userId,
				userLogin,
				userEmail,
				authorId,
				authorName,
			} = (props ?? {}) as AnalyticEventsDataTypes['event_published'];

			const data: AnalyticGtmEventsDataTypes['event_published'] = {
				event_id: `${baseEventId}-${eventInstance}`,
				event_name: eventName,
				event_visibility: eventVisibility,
				event_bundle: eventBundle,
				ticket_type: ticketType,
				...(userEmail ? { customer_email: userEmail } : {}),
				...(userLogin ? { customer_phone: userLogin } : {}),
				...(userId ? { customer_id: userId } : {}),
				organizer_id: authorId,
				...(authorName ? { organizer_name: authorName } : {}),
			};
			payload = data;
			break;
		}

		default:
			payload = props ?? {};
			break;
	}

	return payload;
};

export const gtmAnalyticsEventMapper = (event: CustomEventName<AnalyticEventsDataTypes>): string => {
	const eventMap: Partial<Record<keyof AnalyticEventsDataTypes, string>> = {
		event_page_view: 'view_item',
		add_ticket_to_cart: 'add_to_cart',
		begin_checkout: 'begin_checkout',
		event_purchase: 'purchase',
		initiate_event_creation: 'create_event_initiated',
		event_published: 'event_published',
	};

	return eventMap[event] ?? event;
};
